import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { titleCase } from '../../lib/format'
import type { Organization, UserProfile } from '../../types'
import {
  Alert, Avatar, Badge, Button, Card, CardBody, EmptyState, Input, Select, Skeleton,
} from '../../components/ui'
import { PlusIcon, UserIcon, UsersGroupIcon } from '../../components/icons'

/* Faithful port of anansi/app/templates/admin/users.html + its view
 * admin/routes.py:users(). The Jinja tabs are real GET links that carry every
 * other filter forward in the querystring; here the same effect comes from
 * keeping all five filters (tab included) in one component's state, so
 * switching tabs never drops the others - no need for a URL-synced pattern
 * this codebase doesn't otherwise use (see gov/Users.tsx's identical
 * state-only tab buttons). */

type Tab = '' | 'officer' | 'individual' | 'company'

const TABS: Array<{ value: Tab; label: string }> = [
  { value: '', label: 'All Users' },
  { value: 'officer', label: 'Officers' },
  { value: 'individual', label: 'Individuals' },
  { value: 'company', label: 'Companies / Orgs' },
]

const GOV_ROLES = new Set(['gov_director', 'gov_regional', 'gov_inspector'])
// Mirrors the Jinja template's two hardcoded <select> option sets exactly
// (admin/users.html lines 40-48) rather than the broader dynamic role list
// GET /admin/users?roles echoes back - the view's own filter UI never used
// that list either.
const ALL_TAB_ROLES = ['super_admin', 'gov_director', 'gov_regional', 'gov_inspector', 'team_leader', 'miner']
const OFFICER_TAB_ROLES = ['gov_director', 'gov_regional', 'gov_inspector']

interface AdminUserRow extends UserProfile {
  account_type?: string
}

interface AdminUsersResponse { ok: boolean; items: AdminUserRow[]; roles: string[] }
interface AdminOrgsResponse { ok: boolean; items: Organization[] }

type Tone = 'danger' | 'info' | 'warning' | 'success'

function roleTone(role?: string): Tone {
  if (role === 'super_admin') return 'danger'
  if (GOV_ROLES.has(role ?? '')) return 'info'
  if (role === 'team_leader') return 'warning'
  return 'success'
}

/* Jinja's status pill is a plain green/red binary (not the multi-tone
 * StatusBadge used elsewhere in this app) and relabels "suspended" as
 * "Deactivated" - kept as its own small component so both the account
 * pill and the eventual detail page can match that exactly. */
function AccountStatusPill({ status }: { status?: string }) {
  const s = status || 'active'
  return <Badge tone={s === 'active' ? 'success' : 'danger'}>{s === 'suspended' ? 'Deactivated' : titleCase(s)}</Badge>
}

function isString(v: string | undefined): v is string { return Boolean(v) }

export default function AdminUsers() {
  const [tab, setTab] = useState<Tab>('')
  const [search, setSearch] = useState('')
  const [role, setRole] = useState('')
  const [regionId, setRegionId] = useState('')
  const [districtId, setDistrictId] = useState('')
  const [status, setStatus] = useState('active')

  const usersQuery = useQuery({
    queryKey: ['admin', 'users', role, regionId, status, search],
    queryFn: () => api.get<AdminUsersResponse>(
      `/admin/users?role=${encodeURIComponent(role)}&region_id=${encodeURIComponent(regionId)}&status=${encodeURIComponent(status)}&q=${encodeURIComponent(search)}`,
    ),
    enabled: tab !== 'company',
  })

  const orgsQuery = useQuery({
    queryKey: ['admin', 'orgs-tab', regionId, status],
    queryFn: () => api.get<AdminOrgsResponse>(
      `/admin/orgs?region_id=${encodeURIComponent(regionId)}&status=${encodeURIComponent(status)}&limit=200`,
    ),
    enabled: tab === 'company',
  })

  // No regions/districts catalog endpoint exists anywhere in the JSON API
  // (only rtdb_service.get_regions()/get_districts(), which the Jinja admin
  // view reads directly server-side with no JSON equivalent). Standing in
  // for that catalog: harvest the distinct region_id/district_id values
  // actually present on users, from one unfiltered, long-cached fetch so the
  // dropdowns don't shrink to whatever the active filters happen to return.
  const metaQuery = useQuery({
    queryKey: ['admin', 'users-meta'],
    queryFn: () => api.get<AdminUsersResponse>('/admin/users?status=&limit=1000'),
    staleTime: 5 * 60 * 1000,
  })
  const metaItems = metaQuery.data?.items ?? []
  const regionOptions = Array.from(new Set(metaItems.map((u) => u.region_id).filter(isString))).sort()
  const districtOptions = Array.from(new Set(metaItems.map((u) => u.district_id).filter(isString))).sort()

  const rawUsers = usersQuery.data?.items ?? []
  let people = districtId ? rawUsers.filter((u) => u.district_id === districtId) : rawUsers
  if (tab === 'individual') {
    people = people.filter((u) => !u.org_id && u.account_type !== 'company' && !GOV_ROLES.has(u.role ?? ''))
  } else if (tab === 'officer') {
    people = people.filter((u) => GOV_ROLES.has(u.role ?? ''))
  }

  const rawOrgs = orgsQuery.data?.items ?? []
  let orgs = districtId ? rawOrgs.filter((o) => o.district_id === districtId) : rawOrgs
  if (search) {
    const q = search.toLowerCase()
    orgs = orgs.filter((o) => (o.name ?? '').toLowerCase().includes(q))
  }

  const roleOptions = tab === 'officer' ? OFFICER_TAB_ROLES : ALL_TAB_ROLES

  return (
    <>
      <div className="page-head">
        <div>
          <h1>User Management</h1>
          <p className="page-head__sub">Everyone registered on the platform.</p>
        </div>
      </div>

      <div className="tab-scroll" style={{ marginBottom: 'var(--sp-4)', gap: 'var(--sp-2)' }}>
        {TABS.map((t) => (
          <button
            key={t.value}
            className={`btn btn--sm ${tab === t.value ? 'btn--primary' : 'btn--ghost'}`}
            onClick={() => setTab(t.value)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <Card style={{ marginBottom: 'var(--sp-4)' }}>
        <CardBody>
          <div className="filter-scroll">
            <Input label="Search" placeholder="Name or email…" value={search} onChange={(e) => setSearch(e.target.value)} />
            {tab !== 'company' && (
              <Select label="Role" value={role} onChange={(e) => setRole(e.target.value)}>
                <option value="">All Roles</option>
                {roleOptions.map((r) => <option key={r} value={r}>{titleCase(r)}</option>)}
              </Select>
            )}
            <Select label="Region" value={regionId} onChange={(e) => setRegionId(e.target.value)}>
              <option value="">All Regions</option>
              {regionOptions.map((r) => <option key={r} value={r}>{titleCase(r)}</option>)}
            </Select>
            <Select label="District" value={districtId} onChange={(e) => setDistrictId(e.target.value)}>
              <option value="">All Districts</option>
              {districtOptions.map((d) => <option key={d} value={d}>{titleCase(d)}</option>)}
            </Select>
            <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="active">Active</option>
              <option value="suspended">Deactivated</option>
              <option value="">All</option>
            </Select>
          </div>
        </CardBody>
      </Card>

      <div className="row" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 'var(--sp-3)', marginBottom: 'var(--sp-4)' }}>
        <p className="page-head__sub" style={{ margin: 0 }}>
          {tab === 'company'
            ? `${orgs.length} organisation${orgs.length !== 1 ? 's' : ''} found`
            : `${people.length} user${people.length !== 1 ? 's' : ''} found`}
        </p>
        {/* ONE create entry point: the wizard's own first choice (Officer /
            Leader / Miner / Staff tabs on /users/new) replaces the old
            per-tab button spread - "one button where I need to create
            something; once the wizard opens I can create any user". Inline
            for desktop, thumb-reach FAB on the PWA. */}
        <Link to="/users/new" className="fab-trigger__inline">
          <Button size="sm" icon={<PlusIcon />}>Create</Button>
        </Link>
      </div>
      <Link to="/users/new" className="fab" aria-label="Create user or organisation">
        <PlusIcon size={22} />
      </Link>

      {tab === 'company' ? (
        orgsQuery.isError ? (
          <Alert tone="danger">{(orgsQuery.error as Error).message}</Alert>
        ) : orgsQuery.isLoading ? (
          <div className="stack"><Skeleton h={90} /><Skeleton h={90} /></div>
        ) : orgs.length === 0 ? (
          <EmptyState icon={<UsersGroupIcon size={22} />} title="No organisations found" body="" />
        ) : (
          <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))' }}>
            {orgs.map((o) => <OrgCard key={o.id} org={o} />)}
          </div>
        )
      ) : usersQuery.isError ? (
        <Alert tone="danger">{(usersQuery.error as Error).message}</Alert>
      ) : usersQuery.isLoading ? (
        <div className="stack"><Skeleton h={90} /><Skeleton h={90} /></div>
      ) : people.length === 0 ? (
        <EmptyState icon={<UserIcon size={22} />} title="No users found" body="" />
      ) : (
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))' }}>
          {people.map((u) => <UserCard key={u.uid} user={u} />)}
        </div>
      )}
    </>
  )
}

function UserCard({ user }: { user: AdminUserRow }) {
  return (
    <Card pad hover>
      <div className="row" style={{ alignItems: 'flex-start', gap: 'var(--sp-3)' }}>
        <Avatar name={user.full_name} email={user.email} size={44} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="row" style={{ justifyContent: 'space-between', gap: 'var(--sp-2)' }}>
            <strong style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {user.full_name || '—'}
            </strong>
            <AccountStatusPill status={user.status} />
          </div>
          <p className="page-head__sub" style={{ margin: '2px 0 10px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {user.email || '—'}
          </p>
          <div className="row" style={{ gap: 'var(--sp-2)', flexWrap: 'wrap', alignItems: 'center' }}>
            <Badge tone={roleTone(user.role)}>{titleCase(user.role)}</Badge>
            {user.region_id && (
              <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>{titleCase(user.region_id)}</span>
            )}
          </div>
        </div>
        <Link to={`/users/${user.uid}`} style={{ fontSize: 'var(--text-xs)', fontWeight: 600, flexShrink: 0 }}>View</Link>
      </div>
    </Card>
  )
}

function OrgCard({ org }: { org: Organization }) {
  return (
    <Card pad>
      <div className="row" style={{ alignItems: 'flex-start', gap: 'var(--sp-3)' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="row" style={{ justifyContent: 'space-between', gap: 'var(--sp-2)' }}>
            <strong style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{org.name || '—'}</strong>
            <AccountStatusPill status={org.status} />
          </div>
          <p className="page-head__sub" style={{ margin: '2px 0 10px' }}>{titleCase(org.type)}</p>
          <div className="row" style={{ gap: 'var(--sp-3)', flexWrap: 'wrap', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
            {org.region_id && <span>{titleCase(org.region_id)}{org.district_id ? `, ${titleCase(org.district_id)}` : ''}</span>}
            <span>{org.member_count ?? 0} member{(org.member_count ?? 0) !== 1 ? 's' : ''}</span>
          </div>
        </div>
        <Link to={`/organizations/${org.id}`} style={{ fontSize: 'var(--text-xs)', fontWeight: 600, flexShrink: 0 }}>View</Link>
      </div>
    </Card>
  )
}
